import algoliasearch, { SearchClient, SearchIndex } from 'algoliasearch';
import {
  FBDocTypeEssentials,
  NoSqlDoc,
} from '@rabbit/firebase/doctype';
import {
  GetAlgoliaCreds,
  GetSearchIndexName,
} from './config';
import { SortDirection } from './types';
import { getCachedIndexInfo, getReplicaIndexName } from './index-discovery';

const gSearchIndexCache: Record<string, SearchIndex> = {};
const gSearchClientCache: Record<string, SearchClient> = {};

export function GetAlgoliaSearchClient<TYPE extends NoSqlDoc>(
  type: FBDocTypeEssentials<TYPE>
): SearchClient {
  const { appId, searchApiKey } = GetAlgoliaCreds(type);
  if (!gSearchClientCache[appId]) {
    gSearchClientCache[appId] = algoliasearch(appId, searchApiKey);
  }
  return gSearchClientCache[appId];
}

export async function GetAlgoliaSearchIndex<TYPE extends NoSqlDoc>(
  type: FBDocTypeEssentials<TYPE>,
  sortOptions?: string[]
): Promise<{index: SearchIndex, shouldReverse: boolean}> {
  // Determine the correct index based on sort options
  const { indexName, shouldReverse } = await getSortedIndexInfo(type, sortOptions);

  if (gSearchIndexCache[indexName]) {
    return { index: gSearchIndexCache[indexName], shouldReverse };
  }

  // console.log(`getAlgoliaSearchClient: initIndex - new indexName=${indexName}`);

  const { appId, searchApiKey } = GetAlgoliaCreds(type);
  const client = algoliasearch(appId, searchApiKey);

  gSearchIndexCache[indexName] = client.initIndex(indexName);
  return { index: gSearchIndexCache[indexName], shouldReverse };
}

/**
 * Maps sort options to the correct Algolia index name dynamically
 * Handles tcreate specially since base index is sorted by tcreate:desc
 * @param type - Document type for dynamic discovery
 * @param sortOptions - Array of sort expressions (e.g., ['warranty_startDate:desc'])
 * @returns Promise<{indexName: string, shouldReverse: boolean}>
 */
async function getSortedIndexInfo<TYPE extends NoSqlDoc>(
  type: FBDocTypeEssentials<TYPE>,
  sortOptions?: string[]
): Promise<{indexName: string, shouldReverse: boolean}> {
  const baseIndexName = GetSearchIndexName(type);

  if (!sortOptions || sortOptions.length === 0) {
     // Default index for relevance sorting
    return { indexName: baseIndexName, shouldReverse: false };
  }

  const primarySort = sortOptions[0];
  const [field, direction = 'desc'] = primarySort.split(':') as [string, SortDirection];

  // Special case: tcreate is the default sort of the base index (tcreate:desc)
  if (field === 'tcreate') {
    // console.log(`Using base index for tcreate sort: ${baseIndexName}`);
    return {
      indexName: baseIndexName,
      shouldReverse: direction === 'asc' // Reverse if we want ascending
    };
  }

  // Try to find a replica index for other fields
  const replicaIndexName = await getReplicaIndexName(type, field, direction);

  if (replicaIndexName) {
    const baseIndexName = GetSearchIndexName(type);
    // Check if we're using opposite direction replica (need to reverse results)
    const usingOppositeDirection = !replicaIndexName.endsWith(`_${direction}`);

    return {
      indexName: replicaIndexName,
      shouldReverse: usingOppositeDirection
    };
  } else {
    console.warn(`No replica index for field '${field}', using base index: ${baseIndexName}`);
    return { indexName: baseIndexName, shouldReverse: false };
  }
}

/**
 * Utility function to validate that replica indices exist in Algolia
 * Now uses dynamic discovery instead of hardcoded fields
 */
export async function validateReplicaIndices<TYPE extends NoSqlDoc>(
  type: FBDocTypeEssentials<TYPE>
): Promise<{ exists: string[], supportedFields: string[] }> {
  const indexInfo = await getCachedIndexInfo(type);

  // console.log(`Index validation for ${indexInfo.baseIndex}:`);
  // console.log(`Found ${indexInfo.availableReplicas.length} replica indices`);
  // console.log(`Supported sort fields: ${indexInfo.supportedFields.join(', ')}`);

  return {
    exists: indexInfo.availableReplicas,
    supportedFields: indexInfo.supportedFields
  };
}
